import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import { useDispatch } from "react-redux";
import AxiosInstance from "../../services/networkServices";
import { showToast } from "../../store/slices/common";

/**
 * Same wall clock the Seasons page uses. datetime-local has no timezone, so
 * what an admin types is New York time and is converted to a UTC instant.
 */
const APP_TIMEZONE = "America/New_York";

const CATEGORY_LABELS = {
  karaoke: "Karaoke",
  dance: "Dance",
  fashion: "Fashion",
  makeup: "Makeup",
  art: "Art",
  pets: "Pets",
  rap: "Rap",
};

const cardSx = {
  padding: "20px",
  borderRadius: "10px",
  boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  height: "100%",
  boxSizing: "border-box",
  backgroundColor: "rgba(106, 27, 154, 0.1)",
  borderTop: "5px solid #6A1B9A",
};

const zonedLocalToIso = (local) => {
  if (!local) return null;
  const [datePart, timePart = "00:00"] = local.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  if ([y, m, d, hh, mm].some((part) => Number.isNaN(part))) return null;
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm, 0));
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(guess);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  const wallAsUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second")
  );
  const offsetMs = guess.getTime() - wallAsUtc;
  return new Date(guess.getTime() + offsetMs).toISOString();
};

const formatInZone = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-US", { timeZone: APP_TIMEZONE, timeZoneName: "short" });
};

const formatUtc = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
};

const categoryList = (table) =>
  Object.keys(table || {})
    .map((key) => CATEGORY_LABELS[key] || key)
    .join(", ");

/** One line describing which groups a pending snapshot will replace. */
const summarizeChange = (changes = {}) => {
  const parts = [];
  const hasThresholds =
    changes.voteThresholds ||
    changes.forFunVoteThreshold != null ||
    changes.categoryVoteThresholds;

  if (hasThresholds) {
    const extras = [];
    if (changes.forFunVoteThreshold != null) extras.push("for fun");
    if (changes.categoryVoteThresholds) {
      const names = categoryList(changes.categoryVoteThresholds);
      extras.push(names ? names : "clears category overrides");
    }
    parts.push(`Vote thresholds${extras.length ? ` (${extras.join("; ")})` : ""}`);
  }

  const hasWeights =
    changes.fullEnergyRankWeights ||
    changes.preEnergyRankWeights ||
    changes.consolidatedRankWeights ||
    changes.forFunFeedWeights ||
    changes.categoryRankWeights;

  if (hasWeights) {
    const names = categoryList(changes.categoryRankWeights);
    const extra = changes.categoryRankWeights
      ? names
        ? `category overrides: ${names}`
        : "clears category overrides"
      : null;
    parts.push(`Rank weights${extra ? ` (${extra})` : ""}`);
  }

  return parts.join(" · ") || "Scheduled change";
};

/**
 * Schedule the vote-threshold and rank-weight values currently in the form.
 * The parent validates those fields, posts the snapshot, and puts the form
 * back to the live values so Save does not apply them early.
 */
const ScheduledChanges = ({ disabled, onSchedule }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);
  const [cancelingId, setCancelingId] = useState(null);
  const [items, setItems] = useState([]);
  const [whenLocal, setWhenLocal] = useState("");
  const [includeThresholds, setIncludeThresholds] = useState(true);
  const [includeWeights, setIncludeWeights] = useState(true);

  const load = useCallback(
    async ({ quiet } = {}) => {
      try {
        const response = await AxiosInstance.get("/admin/settings/gameplay/scheduled");
        if (response.data.status) {
          setItems(response.data.data?.scheduledChanges || []);
        } else if (!quiet) {
          dispatch(
            showToast({ color: "error", msg: "Failed to load scheduled changes." })
          );
        }
      } catch (error) {
        console.error("Error loading scheduled gameplay changes:", error);
        if (!quiet) {
          dispatch(
            showToast({ color: "error", msg: "Failed to load scheduled changes." })
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    load();
    const timer = setInterval(() => load({ quiet: true }), 30000);
    return () => clearInterval(timer);
  }, [load]);

  const whenIso = zonedLocalToIso(whenLocal);
  const whenIsFuture = Boolean(whenIso) && new Date(whenIso).getTime() > Date.now();

  const handleSchedule = async () => {
    if (!includeThresholds && !includeWeights) {
      dispatch(
        showToast({
          color: "error",
          msg: "Choose vote thresholds, rank weights, or both.",
        })
      );
      return;
    }
    if (!whenIsFuture) {
      dispatch(
        showToast({
          color: "error",
          msg: "Pick a date and time in the future (New York time).",
        })
      );
      return;
    }

    try {
      setScheduling(true);
      const ok = await onSchedule({
        scheduledAt: whenIso,
        includeThresholds,
        includeWeights,
      });
      if (ok) {
        setWhenLocal("");
        await load({ quiet: true });
      }
    } finally {
      setScheduling(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      setCancelingId(id);
      const response = await AxiosInstance.delete(
        `/admin/settings/gameplay/scheduled/${id}`
      );
      if (response.data.status) {
        dispatch(showToast({ color: "success", msg: "Scheduled change canceled." }));
        await load({ quiet: true });
      } else {
        dispatch(
          showToast({
            color: "error",
            msg: response.data.message || "Failed to cancel that scheduled change.",
          })
        );
      }
    } catch (error) {
      console.error("Error canceling scheduled gameplay change:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error?.response?.data?.message || "Failed to cancel that scheduled change.",
        })
      );
    } finally {
      setCancelingId(null);
    }
  };

  return (
    <Paper sx={cardSx}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <EventIcon sx={{ color: "#6A1B9A" }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Schedule a change
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Apply the vote thresholds and rank weights currently filled in above at
        a later date and time (New York). Live settings stay as they are until
        then. A threshold change restamps in-progress matches at that moment,
        the same way Save does. Rank weights take effect within about 30
        seconds. Other settings on this page are not included. After you
        schedule, the fields you included go back to the live values so Save
        does not apply the future numbers early. If two schedules include the
        same fields, each one replaces those fields when it runs, so the later
        time wins. If the server is down at the chosen time, the change runs
        shortly after it is back.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeThresholds}
              onChange={(e) => setIncludeThresholds(e.target.checked)}
            />
          }
          label="Vote thresholds"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={includeWeights}
              onChange={(e) => setIncludeWeights(e.target.checked)}
            />
          }
          label="Rank weights"
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Vote thresholds include the default table, the for-fun target, and
        category overrides. Rank weights include filling bar, full bar,
        consolidated, the for-fun row, and category weight overrides.
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <TextField
          label={`Date and time (${APP_TIMEZONE})`}
          type="datetime-local"
          size="small"
          value={whenLocal}
          onChange={(e) => setWhenLocal(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText={
            whenIso
              ? `= ${formatUtc(whenIso)}`
              : "The moment these values replace the live ones."
          }
          sx={{ minWidth: "280px" }}
        />
        <Button
          variant="contained"
          onClick={handleSchedule}
          disabled={
            disabled || scheduling || (!includeThresholds && !includeWeights)
          }
          sx={{
            textTransform: "none",
            mt: 0.25,
            backgroundColor: "#6A1B9A",
            "&:hover": { backgroundColor: "#4A148C" },
            "&.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
          }}
        >
          {scheduling ? "Scheduling..." : "Schedule"}
        </Button>
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#142C5C" }}>
        Upcoming
      </Typography>
      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading scheduled changes...
        </Typography>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No vote threshold or rank weight changes are scheduled.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {items.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.72)",
                borderRadius: "8px",
                padding: "10px 12px",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  {formatInZone(item.scheduledAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {summarizeChange(item.changes)}
                </Typography>
              </Box>
              <Button
                size="small"
                color="inherit"
                onClick={() => handleCancel(item.id)}
                disabled={cancelingId === item.id}
                sx={{ textTransform: "none" }}
              >
                {cancelingId === item.id ? "Canceling..." : "Cancel"}
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default ScheduledChanges;
